'use client';

import * as React from 'react';

type Descendant<T = HTMLElement> = {
  element: T | null;
  index: number;
};

interface DescendantContextValue<DescendantType extends Descendant> {
  descendants: Array<DescendantType>;
  registerDescendant(descendant: DescendantType): () => void;
  currentIndex: number;
  incrementIndex(): number;
}

function DescendantProvider<DescendantType extends Descendant>({
  context: Ctx,
  children,
  items,
  set,
}: {
  context: React.Context<DescendantContextValue<DescendantType>>;
  children: React.ReactNode;
  items: DescendantType[];
  set: React.Dispatch<React.SetStateAction<Array<DescendantType>>>;
}) {
  const renderIndexRef = React.useRef(0);
  renderIndexRef.current = 0;

  const registerDescendant = React.useCallback(
    (descendant: DescendantType) => {
      set(cbItems => {
        const existingIndex = cbItems.findIndex(item => item.index === descendant.index);

        if (existingIndex === -1) {
          // Element not found, add new descendant
          return [...cbItems, { ...descendant, index: cbItems.length } as DescendantType];
        } else {
          // Element found, update descendant at the found index
          const updatedItems = [...cbItems];
          updatedItems[existingIndex] = descendant;
          return updatedItems;
        }
      });

      return () => {
        set(cbItems => cbItems.filter(item => item.index !== descendant.index));
      };
    },
    [set]
  );

  const incrementIndex = React.useCallback(() => {
    const current = renderIndexRef.current;
    renderIndexRef.current = current + 1;
    return current;
  }, []);

  const contextValue = React.useMemo(
    () => ({
      descendants: items,
      registerDescendant,
      currentIndex: renderIndexRef.current,
      incrementIndex,
    }),
    [items, registerDescendant, incrementIndex]
  );
  return <Ctx.Provider value={contextValue}>{children}</Ctx.Provider>;
}

function createDescendantContext<DescendantType extends Descendant>(name: string) {
  const ctx = React.createContext<DescendantContextValue<DescendantType>>({
    descendants: [],
    registerDescendant: () => noop,
    currentIndex: 0,
    incrementIndex: () => {
      return 0;
    },
  });
  ctx.displayName = name;
  return ctx;
}

function useDescendant<DescendantType extends Descendant>(
  element: DescendantType['element'],
  context: React.Context<DescendantContextValue<DescendantType>>,
  manualIndex?: number
): number {
  const { registerDescendant, incrementIndex } = React.use(context);

  const indexRef = React.useRef<number | null>(null);
  if (manualIndex !== undefined) {
    indexRef.current = manualIndex;
  } else if (indexRef.current === null || indexRef.current === -1) {
    indexRef.current = incrementIndex();
  }

  const index = indexRef.current;

  React.useLayoutEffect(() => {
    const register = registerDescendant({ element, index } as DescendantType);
    return register;
  }, [element, index, registerDescendant]);

  return index;
}

function noop() {}

export type { Descendant, DescendantContextValue };
export { createDescendantContext, DescendantProvider, useDescendant };
